// Client-side mirror of surveys-server's WidgetConfig assembly (the same
// rules the public /s/{token}/config endpoint applies): the survey's own
// copy and steps, then the selected template's design, then a hard-coded
// placement block. Pure and fetch-free — asset URLs are resolved by the
// caller (preview-panel.tsx, via features/assets/resolve-artwork-urls) and
// handed in already resolved, which is what keeps this synchronously
// testable with plain fixtures instead of a mocked asset client.

import type { StepKind, Template } from "@/api/types";
import type { PlacementInfo, StepInfo, WidgetConfig, WidgetDesign } from "./widget-contract";

const DEFAULT_BACKGROUND_COLOR = "#101014";
const DEFAULT_LOGO_POSITION = "center";

/** The subset of a design template the preview draft actually reads. The
 *  three `*AssetId` fields aren't read by `buildDraft` itself — they ride
 *  along so the panel can key its own live artwork resolution off the same
 *  object it already has in hand, without a second, narrower prop. */
export type DraftTemplate = Pick<
    Template,
    | "backgroundColor"
    | "logoPosition"
    | "fontStack"
    | "colorSlots"
    | "customCss"
    | "backgroundImageAssetId"
    | "logoAssetId"
    | "fontAssetId"
    | "backgroundImageUrl"
    | "logoUrl"
    | "fontFaceUrl"
>;

/** One step as the steps editor's live form state maps onto it — no `id`,
 *  since a draft describes unsaved (possibly never-saved) form values. */
export interface DraftStep {
    kind: StepKind;
    isMetric: boolean;
    /** Absent means not required, as on the widget contract. */
    required?: boolean;
    config: Record<string, unknown>;
}

/**
 * Resolved artwork/font URLs for one draft. Optional and separate from
 * `template` on purpose: this function never fetches anything, so whatever
 * the caller did (or didn't) resolve is passed straight through. Omitted
 * fields fall back to the template's own stored URL (or `null`), which is
 * what keeps a live resolution failure — no asset service, a deleted asset,
 * a blip — from wiping perfectly good artwork the template was saved with.
 */
export interface BuildDraftOptions {
    surveyName: string;
    thankYouText: string | null;
    displayTitle?: string | null;
    displayDescription?: string | null;
    /** The survey's chrome language, so the preview's buttons read the way
     *  the saved widget will. An unknown code renders English there. */
    language: string;
    steps: DraftStep[];
    /** `null` previews the product defaults — an unsaved survey has picked
     *  no template yet, and that must render, not error. */
    template: DraftTemplate | null;
    mode: "kiosk" | "inline";
    assetUrls?: { backgroundImageUrl?: string; logoUrl?: string; fontFaceUrl?: string };
    /** Resolved URLs for the assets choice options reference, by asset id.
     *  Same contract as `assetUrls`: an id that resolved to nothing (null,
     *  or absent) leaves the option's own stored URL in place. */
    optionAssetUrls?: Record<string, string | null>;
}

/**
 * Builds one preview draft — the client-side stand-in for a real
 * `GET /s/{token}/config` response that drives the widget's postMessage
 * preview mode (frontend/widget/src/preview.ts) instead of its normal fetch.
 */
export function buildDraft({
    surveyName,
    thankYouText,
    displayTitle = null,
    displayDescription = null,
    language,
    steps,
    template,
    mode,
    assetUrls = {},
    optionAssetUrls = {},
}: BuildDraftOptions): WidgetConfig {
    const design: WidgetDesign = {
        backgroundColor: template?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
        backgroundImageUrl: assetUrls.backgroundImageUrl ?? template?.backgroundImageUrl ?? null,
        logoUrl: assetUrls.logoUrl ?? template?.logoUrl ?? null,
        logoPosition: template?.logoPosition ?? DEFAULT_LOGO_POSITION,
        fontStack: template?.fontStack ?? null,
        fontFaceUrl: assetUrls.fontFaceUrl ?? template?.fontFaceUrl ?? null,
        colorSlots: template?.colorSlots ?? {},
        templateCss: template?.customCss ?? null,
    };

    // Placement block is fixed rather than read from any real placement: a
    // survey may have several placements (or none yet) with different
    // attract/reset settings, and the preview isn't previewing any one of
    // them. Attract is always OFF here specifically — a real placement's
    // attract screen is what a passerby sees before tapping in, but an
    // operator editing a survey wants the *question* on screen immediately,
    // not an attract loop they'd have to tap past on every keystroke.
    const placement: PlacementInfo = {
        mode,
        attractEnabled: false,
        attractText: null,
        resetSeconds: 15,
        thanksSeconds: 15,
        revisit: "show_submitted",
    };

    return {
        survey: { name: surveyName, thankYouText, displayTitle, displayDescription, language },
        // Synthetic ids and positions: a draft mirrors the editor's live form
        // state, which has no persisted `StepRow` to carry. The widget only
        // needs an id present and stable within one config, and the array
        // order IS the position.
        steps: steps.map(
            (step, index): StepInfo => ({
                id: `draft-${index}`,
                position: index,
                kind: step.kind,
                isMetric: step.isMetric,
                required: step.required ?? false,
                config: withOptionArtwork(step, optionAssetUrls),
            }),
        ),
        placement,
        design,
    };
}

/** A choice option as the draft handles it: only the two fields artwork
 *  resolution reads or writes are typed; everything else rides along. */
interface DraftOption {
    assetId?: string | null;
    assetUrl?: string | null;
}

function draftOptions(step: DraftStep): DraftOption[] {
    if (step.kind !== "choice" || !Array.isArray(step.config.options)) return [];
    return step.config.options as DraftOption[];
}

/** Every asset a choice option references — once each, in first-seen
 *  order — for the caller to resolve and hand back as `optionAssetUrls`. */
export function optionAssetIds(steps: DraftStep[]): string[] {
    const ids: string[] = [];
    for (const step of steps) {
        for (const option of draftOptions(step)) {
            if (option.assetId && !ids.includes(option.assetId)) ids.push(option.assetId);
        }
    }
    return ids;
}

function withOptionArtwork(step: DraftStep, urls: Record<string, string | null>): Record<string, unknown> {
    const options = draftOptions(step);
    if (options.length === 0) return step.config;
    return {
        ...step.config,
        options: options.map((option) => {
            const url = option.assetId ? urls[option.assetId] : undefined;
            return typeof url === "string" ? { ...option, assetUrl: url } : option;
        }),
    };
}
